/**
 * The Toggle Active macro — flip an object's active state.
 *
 * Canonical expansion (read, invert, write):
 *
 *     [get: trace/getActive]  target = <target>
 *         value ──┐
 *     [not: math/not]         a ←────┘
 *         value ──┐
 *     [set: trace/setActive]  target = <target>, state ←┘   ← macro `in` / `out`
 *
 * The three nodes are one intent — nobody opens this to edit the `not` — so it
 * is a macro rather than a sample (DESIGN_PRINCIPLES §6: a macro keeps its
 * identity, a sample spends it).
 *
 * ── Why "Active" and not "Visibility" ──
 *
 * `visibility` has a specific meaning in glTF: `KHR_node_visibility` controls
 * *rendering*. This macro toggles `active`, which is authored scene state —
 * `SceneObject.setActive` writes `_sceneObjectData.active`, updates
 * `_resolvedActive`, and re-scans property names, so it participates in
 * Properties and `$name` expression references. Toggling visibility instead
 * would leave authored `active` untouched and let the two silently disagree.
 * They are different concepts that look alike from the palette, so the name
 * keeps `visibility` free for the KHR meaning rather than squatting on it.
 *
 * Unlike Interval, this expansion is NOT standard-ops-only: `trace/getActive`
 * and `trace/setActive` are TRACE_interactivity ops, because glTF has no
 * standard pointer for "this object is active" in our sense. The macro pattern
 * still applies unchanged — one logical block, metadata-tagged expansion,
 * degrade-on-edit — it is just portable across Trace clients rather than to
 * every conformant viewer.
 */

import { GraphOps } from '../../../scenedata/behaviors/behaviorgraphdata';
import { registerMacro } from './registry';
import type { MacroDefinition } from './registry';

/** Macro name for the Toggle Active canned composition. */
export const TOGGLE_ACTIVE_MACRO = 'toggleActive';

/** Catalog pseudo-op naming Toggle Active as a single high-level node. */
export const TOGGLE_ACTIVE_MACRO_OP = 'macro/toggle-active' as const;

/** The macro's addressing parameter — the object whose active state flips. */
export const TOGGLE_ACTIVE_TARGET_PARAM = 'target';

export const TOGGLE_ACTIVE_MACRO_DEF: MacroDefinition = {
    name: TOGGLE_ACTIVE_MACRO,
    op: TOGGLE_ACTIVE_MACRO_OP,
    nodes: [
        { role: 'get', op: GraphOps.TRACE_GET_ACTIVE },
        { role: 'not', op: GraphOps.MATH_NOT },
        { role: 'set', op: GraphOps.TRACE_SET_ACTIVE },
    ],
    // Flow passes straight through the write; the read and inversion are pull
    // evaluated from it, so there is no internal flow wiring.
    wires: [],
    valueWires: [
        { from: 'get', socket: 'value', to: 'not', toSocket: 'a' },
        { from: 'not', socket: 'value', to: 'set', toSocket: 'state' },
    ],
    entryRole: 'set',
    inputs: { in: { role: 'set', socket: 'in' } },
    defaultInput: 'in',
    outputs: { out: { role: 'set', socket: 'out' } },
    params: {
        [TOGGLE_ACTIVE_TARGET_PARAM]: {
            // Both ends must address the same object, or the macro reads one
            // object's state and writes another's.
            targets: [
                { role: 'get', socket: 'target' },
                { role: 'set', socket: 'target' },
            ],
            kind: 'config',
            validateLiteral: value =>
                typeof value === 'string' && value.length > 0 ? null : 'must be a scene object path',
        },
    },
    // Nothing to disarm: with no internal flow wiring, an expansion with a bad
    // target simply resolves to no object and does nothing. The write is the
    // macro's own entry, so dropping it would sever the author's chain.
    disarm: [],
};

registerMacro(TOGGLE_ACTIVE_MACRO_DEF);
